import _ from 'lodash'
import type {CreateExtArgs} from '@wix/document-manager-core'
import type {
    ComponentFeature,
    CompRef,
    DalValue,
    DependenciesManager,
    FragmentPointer,
    Fragments,
    FragmentsOptions,
    RawComponentExport,
    RawComponentExportStructure,
    RawImportExportOptions
} from '@wix/document-services-types'
import {
    STRUCTURE_PROPERTIES_TO_KEEP,
    BASE_STRUCTURE_PROPERTIES,
    COMPONENT_QUERIES,
    COMPONENT_QUERY_TO_NAMESPACE_MAP
} from '../constants'
import {forEachReferredDataItem} from '@wix/import-export-utils'
import {updateChildren} from './utils/children'
import {updateComponentDataItem} from './utils/dataItems'
import {updateOverrides} from './utils/overrides'
import {getComponentFeaturesByType, isSystemDataItem, verifyInstalledApps} from '../utils'
import {updateGlobalData} from './utils/globalData'
import {updateFixerVersions} from './utils/fixerVersions'
import {getDependenciesManager, SPECIAL_DATA_NODE_REFERENCE_MAP} from './utils/dependencies'
import {applyMatchers} from './matchers'
import {DATA_TYPES} from '../../../../constants/constants'
import type {DataModelExtensionAPI} from '../../../dataModel/dataModel'

interface IdsByNamespace {
    [namespace: string]: Set<string>
}

interface DataItemWithId {
    id: string
}

const PRE_REGISTERED_OWNED_DATA_ITEM_NAMESPACES = new Set<string>([DATA_TYPES.variants, DATA_TYPES.variables])

const getIdsByNamespace = (idsByNamespace: IdsByNamespace, namespace: string): Set<string> =>
    (idsByNamespace[namespace] ??= new Set<string>())

const isPreRegisteredOwnedDataItem = (item: DalValue, namespace: string): item is DataItemWithId =>
    _.isPlainObject(item) && typeof item.id === 'string' && PRE_REGISTERED_OWNED_DATA_ITEM_NAMESPACES.has(namespace)

const preRegisterOwnedDataItems = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    component: RawComponentExportStructure,
    references: DependenciesManager['references'],
    {componentFeatures}: RawImportExportOptions
): void => {
    const {coreConfig, extensionAPI} = extensionContext
    const {schemaService} = coreConfig
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const features = getComponentFeaturesByType(component.structure.componentType, componentFeatures)
    const ownedSourceIdsByNamespace: IdsByNamespace = {}
    const reservationOnlyTargetIdsByNamespace: IdsByNamespace = {}

    _.forOwn(features, feature => {
        const dataItem = component.data?.[feature.alias]
        if (!dataItem) {
            return
        }

        forEachReferredDataItem(schemaService, dataItem, feature.namespace, ({item, namespace}) => {
            if (isPreRegisteredOwnedDataItem(item, namespace)) {
                getIdsByNamespace(ownedSourceIdsByNamespace, namespace).add(item.id)
            }
        })
    })

    _.forOwn(features, feature => {
        const dataItem = component.data?.[feature.alias]
        if (!dataItem) {
            return
        }

        const originalDataItem = dataModel.components.getItemByQuery(componentPointer, feature.query, {raw: true})

        forEachReferredDataItem(
            schemaService,
            dataItem,
            feature.namespace,
            ({item, namespace, originalItem}) => {
                if (isPreRegisteredOwnedDataItem(item, namespace)) {
                    const sourceId = item.id
                    const ownedSourceIds = ownedSourceIdsByNamespace[namespace] ?? new Set<string>()
                    const reservationOnlyTargetIds = getIdsByNamespace(reservationOnlyTargetIdsByNamespace, namespace)

                    let targetId = references.getTarget(sourceId, namespace)
                    if (
                        targetId === sourceId &&
                        reservationOnlyTargetIds.has(sourceId) &&
                        ownedSourceIds.has(sourceId)
                    ) {
                        targetId = undefined
                    }

                    if (!targetId) {
                        if (_.isPlainObject(originalItem) && typeof originalItem.id === 'string') {
                            targetId = originalItem.id
                            references.addReference(sourceId, namespace, {target: targetId})
                        } else {
                            targetId = references.getNewItemId(sourceId, namespace)
                        }
                    }

                    if (!targetId) {
                        return
                    }

                    if (!references.getTarget(targetId, namespace)) {
                        reservationOnlyTargetIds.add(targetId)
                        if (!ownedSourceIds.has(targetId)) {
                            references.addReference(targetId, namespace, {target: targetId})
                        }
                    }
                }
            },
            {originalItem: originalDataItem}
        )
    })
}

const updateDataItems = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    component: RawComponentExportStructure,
    dependenciesManager: DependenciesManager,
    {componentFeatures}: RawImportExportOptions
) => {
    const {dal, pointers, extensionAPI} = extensionContext
    const {componentType} = component.structure

    const features = getComponentFeaturesByType(componentType, componentFeatures)
    _.forOwn(features, (feature: ComponentFeature) => {
        const {query, namespace, alias} = feature

        const dataItem = component.data[alias]

        updateComponentDataItem(
            extensionContext,
            componentPointer,
            dataItem,
            feature,
            dependenciesManager,
            componentType
        )

        const exportQueryValue = component.structure[query]
        if (isSystemDataItem(exportQueryValue, namespace, extensionAPI)) {
            const queryPointer = pointers.getInnerPointer(componentPointer, query)
            dal.set(queryPointer, exportQueryValue)
        }
    })
}

const updateComponentStructure = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    componentStructure: RawComponentExportStructure['structure']
) => {
    const {dal, extensionAPI} = extensionContext
    const origStructure = dal.get(componentPointer)
    const newStructure = {
        ..._.pick(origStructure, ...BASE_STRUCTURE_PROPERTIES),
        ...componentStructure,
        ..._.pickBy(origStructure, (value: DalValue, property: string) => {
            if (STRUCTURE_PROPERTIES_TO_KEEP.has(property)) {
                return true
            }

            if (COMPONENT_QUERIES.has(property)) {
                const namespace = COMPONENT_QUERY_TO_NAMESPACE_MAP[property]
                return !isSystemDataItem(value, namespace, extensionAPI)
            }
        })
    }

    if (origStructure.components === undefined) {
        delete newStructure.components
    }

    dal.set(componentPointer, newStructure)
}

const updateMobileStructure = ({dal, pointers}: CreateExtArgs, componentPointer: CompRef) => {
    const mobilePointer = pointers.structure.getMobilePointer(componentPointer)
    const origMobileStructure = dal.get(mobilePointer)
    if (origMobileStructure) {
        const componentStructure = dal.get(componentPointer)

        const newStructure = {
            ...componentStructure,
            components: []
        }

        if (origMobileStructure.components === undefined) {
            delete newStructure.components
        }

        if (origMobileStructure.components) dal.set(mobilePointer, newStructure)
    }
}

export const importAllComponents = (
    extensionContext: CreateExtArgs,
    componentId: RawComponentExport['rootComponent'],
    componentExport: RawComponentExport,
    componentToReplace: CompRef,
    dependenciesManager: DependenciesManager,
    options: RawImportExportOptions
) => {
    const {components} = componentExport
    const component = components[componentId]
    const {references, applications} = dependenciesManager

    references.addReference(componentId, componentToReplace.type, {target: componentToReplace.id})

    verifyInstalledApps(applications.getRequiredAppIdsByComponents(components), extensionContext.extensionAPI)

    updateComponentStructure(extensionContext, componentToReplace, component.structure)
    preRegisterOwnedDataItems(extensionContext, componentToReplace, component, references, options)
    updateChildren(
        extensionContext,
        componentToReplace,
        component.structure,
        componentExport,
        dependenciesManager,
        options
    )
    updateDataItems(extensionContext, componentToReplace, component, dependenciesManager, options)
    updateOverrides(extensionContext, componentToReplace, component, dependenciesManager, options)
    updateMobileStructure(extensionContext, componentToReplace)
}

export const importRawComponent = (
    extensionContext: CreateExtArgs,
    componentId: RawComponentExport['rootComponent'],
    componentExport: RawComponentExport,
    componentToReplace: CompRef,
    options: RawImportExportOptions
) => {
    const {rootComponent, components, dependencies, globalData, fixerVersions} = componentExport
    const component = components[rootComponent]

    const dependenciesManager: DependenciesManager = getDependenciesManager(
        _.merge({}, {references: SPECIAL_DATA_NODE_REFERENCE_MAP}, dependencies)
    )

    if (options.useDefaultMatchers) {
        applyMatchers(extensionContext, componentToReplace, dependenciesManager)
    }

    updateGlobalData(extensionContext, componentToReplace, globalData, dependenciesManager)
    updateFixerVersions(extensionContext, componentToReplace, component, fixerVersions, dependenciesManager)
    importAllComponents(
        extensionContext,
        componentId,
        componentExport,
        componentToReplace,
        dependenciesManager,
        options
    )
}

export const replaceFragments = (
    extensionContext: CreateExtArgs,
    fragments: Fragments,
    options?: FragmentsOptions
): void => {
    const {dal, coreConfig} = extensionContext

    const isFragmentInstanceValid = (fragmentType: string, fragmentInstance: string) => {
        if (!coreConfig.supportedFragments?.[fragmentType]?.singletonInstance) {
            return true
        }
        return coreConfig.supportedFragments?.[fragmentType]?.singletonInstance === fragmentInstance
    }

    for (const [fragmentType, instances] of Object.entries(fragments)) {
        const allowedInstances = options?.[fragmentType]

        for (const [fragmentInstance, namespaces] of Object.entries(instances)) {
            if (allowedInstances?.length && !allowedInstances.includes(fragmentInstance)) {
                continue
            }

            if (!isFragmentInstanceValid(fragmentType, fragmentInstance)) {
                continue
            }

            for (const [type, items] of Object.entries(namespaces)) {
                for (const [id, item] of Object.entries(items)) {
                    const pointer: FragmentPointer = {
                        id,
                        type,
                        fragmentType,
                        fragmentInstance
                    }

                    dal.set(pointer, item)
                }
            }
        }
    }
}
